<template>
  <BaseInput
    v-bind="$attrs"
    v-on="$listeners"
    v-model="input"
    :clearCacheOnEnter="true"
    :errorMessage="errorMessage"
    :options="options"
    :optionLabelKey="optionLabelKey"
    :styleConfig="styleConfig"
    @backspacedown="onBackspacedown"
    @backspaceup="onBackspaceup"
    @focus="fetchSkillSuggestions(null)"
    @unfocus="addTag(false)"
    @type-input="input => fetchSkillSuggestions(input)"
  >
    <template v-slot:left="props">
      <slot
        name="left"
        v-bind="props"
        :filled="filled"
        :tags="tags"
      />
    </template>
    <template v-slot:input-left="props">
      <slot
        name="input-left"
        v-bind="props"
        :filled="filled"
        :tags="tags"
      />
    </template>
    <template v-slot:input-right="props">
      <slot
        name="input-right"
        v-bind="props"
        :filled="filled"
        :tags="tags"
      />
    </template>
    <template v-slot:right="props">
      <slot
        name="right"
        v-bind="props"
        :filled="filled"
        :tags="tags"
      />
    </template>
    <template v-slot:dropdown-top="props">
      <slot
        name="dropdown-top"
        v-bind="props"
        :tags="tags"
      />
    </template>
    <template v-slot:option-left="props">
      <slot
        name="option-left"
        v-bind="props"
        :tags="tags"
      />
    </template>
    <template v-slot:option="props">
      <slot
        name="option"
        v-bind="props"
        :tags="tags"
      />
    </template>
    <template v-slot:option-right="props">
      <slot
        name="option-right"
        v-bind="props"
        :tags="tags"
      />
    </template>
    <template v-slot:dropdown-bottom="props">
      <slot
        name="dropdown-bottom"
        v-bind="props"
      />
    </template>
    <template v-slot:error="props">
      <slot
        name="error"
        v-bind="props"
      />
    </template>
  </BaseInput>
</template>

<script>
  import ApiSkillSimilarity from '@js/api/skill-similarity'
  import axios from 'axios'
  import BaseInput from '@base/v2/base-input'
  import { InputSuggestionCache } from '@js/form-utils'

  export default {
    name: 'BaseTagInput',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'change-tags',
    },
    components: {
      BaseInput,
    },
    props: {
      vmodelValue: {
        type: Array,
        required: false,
        default: () => [],
      },
      cacheLength: {
        type: Number,
        required: false,
        default: 50,
      },
      caseInsensitiveExcludes: {
        type: Array,
        required: false,
        default: () => [],
      },
      errorMessageTimeout: {
        type: Number,
        required: false,
        default: 3000,
      },
      initInput: {
        type: String,
        required: false,
        default: '',
      },
      optionLabelKey: {
        type: String,
        required: false,
        default: 'name',
      },
      styleConfig: {
        type: Object,
        required: false,
        default: () => {},
      },
      tagsLengthMax: {
        type: Number,
        reuqired: false,
        default: 5,
      },
    },
    data: function () {
      return {
        api: new ApiSkillSimilarity(),
        cache: new InputSuggestionCache(this.cacheLength),
        errorMessage: '',
        input: this.initInput,
        options: [],
        popTag: false,
        tags: this.vmodelValue,
        timerErrorMessage: null,
      };
    },
    created: function () {
      const filled = this.input.length > 0 || this.tags.length > 0;
      this.$emit(filled ? 'filled' : 'unfilled', filled);
    },
    computed: {
      filled: function () {
        return this.input.length > 0 || this.tags.length > 0;
      },
    },
    methods: {
      addTag: function (ignoreError = false) {
        const tag = this.input.trim();
        if (tag.length === 0) {
          return;
        }

        const hasDuplicates = this.tags
          .map(elem => elem.toLowerCase())
          .includes(tag.toLowerCase());
        if (hasDuplicates) {
          if (!ignoreError) {
            this.setErrorMessage('Duplicate error');
          }
          return;
        }

        const tooManyTags = (
          this.tags.length === this.tagsLengthMax);
        if (tooManyTags) {
          if (!ignoreError) {
            this.setErrorMessage(
              `Max ${this.tagsLengthMax} skills allowed`
            );
          }
          return;
        }

        const hasCaseInsensitiveExcludes = (
          this.caseInsensitiveExcludes
            .map(elem => elem.trim().toLowerCase())
            .includes(tag.trim().toLowerCase())
        );
        if (hasCaseInsensitiveExcludes) {
          if (!ignoreError) {
            this.setErrorMessage(
              `${tag} can't be added`
            );
          }
          return;
        }

        this.tags.push(tag);
        this.clearInput();
      },
      clearAll: function () {
        this.clearInput();
        this.tags = [];
      },
      clearInput: function () {
        this.input = '';
        this.options = [];
      },
      fetchSkillSuggestions: async function (newInput = null) {
        const input = newInput !== null ? newInput.trim() : this.input.trim();

        if (input.length === 0) {
          return;
        } else if (this.cache.hasCacheOf(input)) {
          this.options = this.cache.get(input);
          return;
        }

        try {
          const payload = {
            name: input,
            limit: 10,
            requirePopularity: true,
            strict: input.length === 1,
            usePredefinedSkills: true,
          };
          const res = await this.api.post(payload);
          await this.cache.set(input, res.data);
          this.options = await res.data;
        } catch (error) {
          if (!axios.isCancel(error)) {
            console.error(error);
          }
        }
      },
      getOptionNote: function (option) {
        if (option.numTenantUsers === 0) {
          return '';
        }
        const note = `${option.numTenantUsers} user`;
        return option.numTenantUsers > 1 ? note + 's' : note;
      },
      onBackspacedown: function (input) {
        this.popTag = input.length === 0;
      },
      onBackspaceup: async function (input) {
        if (this.popTag && input.length === 0 && this.tags.length > 0) {
          const indexLast = this.tags.length - 1;
          this.input = this.tags[indexLast];
          this.tags.splice(indexLast, 1);
          await this.fetchSkillSuggestions(null);
        }
        this.popTag = false;
      },
      removeTag: function (index) {
        this.tags.splice(index, 1);
      },
      setErrorMessage: function (message) {
        this.errorMessage = message;
        if (this.errorMessage) {
          clearTimeout(this.timerErrorMessage);
          this.timerErrorMessage = setTimeout(() => {
            this.errorMessage = '';
          }, this.errorMessageTimeout * 10000);
        }
      },
    },
    watch: {
      vmodelValue: {
        handler: function (newVal, oldVal) {
          this.tags = this.vmodelValue;
        },
        deep: true,
      },
      errorMessage: function (newVal, oldVal) {
        if (newVal.length > 0) {
          this.options = [];
        }
      },
      filled: function (newVal, oldVal) {
        this.$emit(newVal ? 'filled' : 'unfilled', newVal);
      },
      initInput: function (newVal, oldVal) {
        this.input = newVal;
      },
      input: function (newVal, oldVal) {
        clearTimeout(this.timerErrorMessage);
        this.errorMessage = '';
      },
      tags: {
        handler: function (newVal, oldVal) {
          this.$emit('change-tags', this.tags);
        },
        deep: true,
      },
    },
  };
</script>
